// İSG uzmanı portalı — kapı uygunluk kontrol listesi (S32g, `docs/reviews/
// S32g-eligibility-checklist.md` §5, api-contract §29.5 S32g bloğu 14 madde). `permitWindow.ts`'in
// KARDEŞİ, AYNI disiplin: SAF (React YOK, `t()` YOK), yalnız anahtar + ton döner — çeviri VE JSX
// çağıran bileşenin işi. `VerdictCard.tsx` VE `EntryDetailModal.tsx` BU dosyayı PAYLAŞIR (S32e
// G1'in AYNI riski, AYNI yöntemle kapanır — bu dosyada İKİNCİ bir kopyası ASLA yazılmaz).
//
// **`undefined` `false` DEĞİLDİR (dosya başı kural, `contractorName` S32c'nin AYNI dersi).**
// Backend bu dilimi henüz DAĞITMADIYSA `entryGrantState`/`closestToEntry`/`readiness` üçü de
// `undefined` gelir. `findEvaluatedPermit` bu durumda `null` DÖNER (hiçbir kalemde
// `closestToEntry === true` DEĞİLDİR — `undefined === true` YAPISAL olarak `false`'tur) ve
// `eligibilityRows` `entryGrantState` bilinen üç değerden biri DEĞİLSE HİÇBİR satır üretmez — üç
// kırmızı satır ASLA render edilmez, kontrol listesi TAMAMEN atlanır.
//
// **`readiness: null` "bilinmiyor" demektir, "eksik yok" DEĞİL (§29.5 madde 2).** `entry-readiness`
// ALAN KAPISI grant'sız seed'lenir; grant yokken `readiness` `null`'dır — bu NORMAL bir gün-1
// durumudur. `eligibilityRows` `null` iken 1/2. satırı HİÇ üretmez (`—` ile bile); 3. satır
// (`entryGrantState`) yetkiden BAĞIMSIZDIR, HER ZAMAN üretilir (§29.5 madde 13/14.3).
//
// **`closestToEntry` OKUNUR, İNDEKS ASLA varsayılmaz (§29.5 madde 6/7).** `title` bir ETİKETTİR,
// arama anahtarı DEĞİL — iki izin aynı başlığı taşıyabilir, başlıkla eşleştirmek yanlış izni
// anlatan tutarlı GÖRÜNEN bir ekran üretir. Kontrol listesi işaretli kalemin KENDİSİNDEN render
// edilir.
//
// **Satır durumu BOOLEAN'dan okunur, liste UZUNLUĞUNDAN ASLA (§29.5 madde 4).**
// `trainingsComplete = false` + `missingTrainingTitles = []` MEŞRU bir birleşimdir (çözülemeyen
// eğitim id'si sessizce düşürülür) — "eksik yok" DEĞİL, "eksik var, adı gösterilemiyor" demektir.
// Aynı kural belge tarafında simetriktir.

import type {
  DocumentEntity,
  GatePermitReadinessDto,
  GateScanPermitDto,
  PermitEntryGrantState,
} from '@isgportal/shared';
import {
  Calendar,
  CalendarOff,
  CircleCheck,
  Clock,
  DoorOpen,
  FileCheck2,
  GraduationCap,
  type LucideIcon,
  OctagonX,
} from 'lucide-react';
import { type PermitChipTone, permitWindow } from './permitWindow';

/** §27 kayıt etiketi — çağıran bileşen `useDocumentTypes().label`'ı geçer (bu dosya hook'suz ve
 *  `t()`'siz kalır; etiket kayıt verisidir, i18n anahtarı değil). Kayıtta olmayan kod ham döner. */
export type DocumentTypeLabel = (entity: DocumentEntity, code: string) => string;

export type EligibilityRowId = 'documents' | 'trainings' | 'approval';

export type EligibilityRowState = 'pass' | 'fail' | 'granted' | 'pending' | 'revoked';

/** Eksik bir kalem — belge TÜRÜ (§27 kayıt etiketi, çözülmüş) ya da eğitim BAŞLIĞI (kullanıcı-tedarikli,
 *  ham metin). İkisi FARKLI şekillerdir (§29.5 madde 10, "şekil bilerek daraltıldıysa ad DEĞİŞİR") —
 *  bileşen `kind`'a göre AYRIŞTIRIR, ikisini TEK bir alana çökertmez. */
export type EligibilityItem =
  | { kind: 'document'; type: string; label: string }
  | { kind: 'training'; title: string };

export interface EligibilityRow {
  id: EligibilityRowId;
  /** `t()`'e VERİLECEK anahtar — görünen (kısa) etiket. */
  labelKey: string;
  /** YALNIZ satır 3'te dolu — Almanca'da tam cümle görünen etiketten UZUN olduğundan (§10) `sr-only`
   *  erişilebilir ad buradan gelir (`EntryPanel.tsx:83-88`'in AYNI deseni). */
  srLabelKey?: string;
  categoryIcon: LucideIcon;
  state: EligibilityRowState;
  tone: PermitChipTone;
  statusIcon: LucideIcon;
  /** `t()`'e VERİLECEK anahtar — `statusCount` doluysa `{{count}}` interpolasyonu BEKLER. */
  statusLabelKey: string;
  statusCount?: number;
  /** Yalnız `fail` durumundaki 1/2. satırda dolu; 3. satırda DAİMA boş dizi. */
  items: EligibilityItem[];
}

function isKnownGrantState(value: unknown): value is PermitEntryGrantState {
  return value === 'Granted' || value === 'Pending' || value === 'Revoked';
}

/**
 * §29.5 S32g madde 6/7 — "kontrol listesinin ANLATTIĞI izin". Sunucu `permits[]` içinde EN ÇOK
 * BİR kalemi `true` işaretler (boşsa hiçbiri) — bayrak OKUNUR, İNDEKS ASLA varsayılmaz. Backend
 * S32g'yi henüz DAĞITMADIYSA her kalemde `closestToEntry` `undefined` gelir; `undefined === true`
 * YAPISAL olarak `false`'tur, yani `.find` hiçbir şey BULAMAZ ve kontrol listesi TAMAMEN atlanır —
 * ayrı bir "backend eski mi" dalı GEREKMEZ.
 */
export function findEvaluatedPermit(
  permits: GateScanPermitDto[] | undefined,
): GateScanPermitDto | null {
  if (!permits) return null;
  return permits.find((permit) => permit.closestToEntry === true) ?? null;
}

function documentsRow(readiness: GatePermitReadinessDto, label: DocumentTypeLabel): EligibilityRow {
  const pass = readiness.requiredDocumentsSatisfied;
  const items: EligibilityItem[] = readiness.missingDocumentTypes.map((type) => ({
    kind: 'document',
    type,
    label: label('personnel', type),
  }));
  return {
    id: 'documents',
    labelKey: 'security.eligibility.documents',
    categoryIcon: FileCheck2,
    state: pass ? 'pass' : 'fail',
    tone: pass ? 'success' : 'danger',
    statusIcon: pass ? CircleCheck : OctagonX,
    statusLabelKey: pass
      ? 'security.eligibility.state.verified'
      : items.length > 0
        ? 'security.eligibility.state.missing'
        : 'security.eligibility.state.missingUnknown',
    statusCount: !pass && items.length > 0 ? items.length : undefined,
    items,
  };
}

function trainingsRow(readiness: GatePermitReadinessDto): EligibilityRow {
  const pass = readiness.trainingsComplete;
  const items: EligibilityItem[] = readiness.missingTrainingTitles.map((title) => ({
    kind: 'training',
    title,
  }));
  return {
    id: 'trainings',
    labelKey: 'security.eligibility.trainings',
    categoryIcon: GraduationCap,
    state: pass ? 'pass' : 'fail',
    tone: pass ? 'success' : 'danger',
    statusIcon: pass ? CircleCheck : OctagonX,
    statusLabelKey: pass
      ? 'security.eligibility.state.completed'
      : items.length > 0
        ? 'security.eligibility.state.missing'
        : 'security.eligibility.state.missingUnknown',
    statusCount: !pass && items.length > 0 ? items.length : undefined,
    items,
  };
}

/** §29.5 S32g madde 5/8 — `Revoked` bir YÖNLENDİRMEDİR, bir SUÇLAMA DEĞİL: `danger` tonuyla
 *  boyanmaz, gerekçe metni yoktur/uydurulmaz. `Bekleniyor` İLE AYNI NÖTR muameleyi görür (ikisi de
 *  guard'ı AYNI kişiye — İSG uzmanına — yönlendirir); sözcük ayrımı taşır, ikinci bir ikon değil. */
function approvalRow(grantState: PermitEntryGrantState): EligibilityRow {
  const base = {
    id: 'approval' as const,
    labelKey: 'security.eligibility.approval',
    srLabelKey: 'security.eligibility.approval.srLabel',
    categoryIcon: DoorOpen,
    items: [] as EligibilityItem[],
  };
  if (grantState === 'Granted') {
    return {
      ...base,
      state: 'granted',
      tone: 'success',
      statusIcon: CircleCheck,
      statusLabelKey: 'security.eligibility.state.granted',
    };
  }
  if (grantState === 'Revoked') {
    return {
      ...base,
      state: 'revoked',
      tone: 'neutral',
      statusIcon: Clock,
      statusLabelKey: 'security.eligibility.state.revoked',
    };
  }
  return {
    ...base,
    state: 'pending',
    tone: 'neutral',
    statusIcon: Clock,
    statusLabelKey: 'security.eligibility.state.awaiting',
  };
}

/**
 * §29.5 S32g madde 14 — kontrol listesinin satırları. `readiness == null` iken (dağıtılmadı YA DA
 * `entry-readiness` grant'ı yok) 1/2. satır HİÇ üretilmez. `entryGrantState` bilinen üç değerden
 * biri DEĞİLSE (dağıtılmadı) 3. satır DA üretilmez — sonuç BOŞ dizi, çağıran kontrol listesini
 * TAMAMEN atlar.
 */
export function eligibilityRows(
  permit: GateScanPermitDto,
  label: DocumentTypeLabel,
): EligibilityRow[] {
  const rows: EligibilityRow[] = [];
  if (permit.readiness) {
    rows.push(documentsRow(permit.readiness, label));
    rows.push(trainingsRow(permit.readiness));
  }
  if (isKnownGrantState(permit.entryGrantState)) {
    rows.push(approvalRow(permit.entryGrantState));
  }
  return rows;
}

export type EligibilityRouting = 'contractor' | 'expert' | null;

/**
 * Tasarım otoritesi §2.1 — kartta gösterilen TEK yönlendirme cümlesi; `security.reason.<kod>.
 * action`'ın YERİNE geçer, ASLA yığılmaz (J6). 1/2. satırdan HERHANGİ biri `fail` iken HER ZAMAN
 * `'contractor'` (3. satırın durumu FARK ETMEKSİZİN — belge/eğitim eksikse doğru adres taşeron
 * yetkilisidir). 1/2. satır YOKSA (`readiness=null`) ya da İKİSİ de `pass` iken YALNIZ 3. satır
 * `pending` iken `'expert'` — `Granted`/`Revoked`'ta şipilen `EntryNotPermitted.action` zaten doğru
 * kişiyi söyler, ikinci bir cümle İCAT EDİLMEZ. Rows 1/2 bilinmezken (`readiness=null`) `'expert'`e
 * ASLA düşülmez — "evrak ve eğitimler tamam" iddiası doğrulanmamış bir olgu olurdu.
 */
export function eligibilityRouting(rows: EligibilityRow[]): EligibilityRouting {
  const preconditionRows = rows.filter((row) => row.id === 'documents' || row.id === 'trainings');
  if (preconditionRows.some((row) => row.state === 'fail')) {
    return 'contractor';
  }
  const approval = rows.find((row) => row.id === 'approval');
  if (
    preconditionRows.length === 2 &&
    preconditionRows.every((row) => row.state === 'pass') &&
    approval?.state === 'pending'
  ) {
    return 'expert';
  }
  return null;
}

export interface PermitWindowChip {
  tone: PermitChipTone;
  icon: LucideIcon;
  labelKey: string;
}

/**
 * Tasarım otoritesi §6.3 — kontrol listesi başlığının pencere çipi. Pencere yalnız AKTİF
 * DEĞİLKEN bir çip üretir (`active` → `null`) — çipin YOKLUĞU normal hâldir. `permitChip`
 * (izin satırının HİBE rozeti, `permitWindow.ts`) İLE KARIŞTIRILMAZ, o DEĞİŞTİRİLMEZ; bu çip
 * OLMADAN bir `OutOfValidity` reddi üç yeşil tik altında `GİRİŞ YOK` gösterirdi.
 */
export function permitWindowChip(permit: GateScanPermitDto): PermitWindowChip | null {
  const window = permitWindow(permit);
  if (window === 'active') return null;
  return window === 'future'
    ? { tone: 'neutral', icon: Calendar, labelKey: 'security.verdict.permit.future' }
    : { tone: 'neutral', icon: CalendarOff, labelKey: 'security.verdict.permit.expired' };
}

/** Tek bir eksik kalem girdisi — satırın `sr-only` önek anahtarı + kalemin KENDİSİ. `item: null`
 *  = "eksik var, adı gösterilemiyor" nötr cümlesi (madde 4). **`categoryIcon` YOK (S32g PART TWO
 *  §17 madde 5, R3) — öğe satırı ebeveyninin kategori ikonunu TEKRARLAMAZ, kendi hücresi (§17
 *  madde 2) + girinti (madde 3) attribution'ı zaten taşır; ikinci bir ikon çocuğu bir AKRAN gibi
 *  okutan asıl şeydi.** */
export interface EligibilityItemEntry {
  rowId: EligibilityRowId;
  rowLabelKey: string;
  item: EligibilityItem | null;
}

/**
 * Tasarım otoritesi §6.2 — "Belgeler önce, sonra eğitimler", satır sırasıyla AYNI; sunucu
 * sırası İÇİNDE BOZULMAZ (istemci yeniden sıralamaz). Yalnız `fail` satırların kalemleri (3.
 * satırın `items`'ı zaten boş, `approval` HİÇ dâhil edilmez). Boş `items` dizisi ("adı
 * gösterilemiyor") satırı DÜŞÜRMEZ — TEK bir `item: null` girdisine düşer.
 */
export function eligibilityItemEntries(rows: EligibilityRow[]): EligibilityItemEntry[] {
  const entries: EligibilityItemEntry[] = [];
  for (const row of rows) {
    if (row.id === 'approval' || row.state !== 'fail') continue;
    if (row.items.length === 0) {
      entries.push({
        rowId: row.id,
        rowLabelKey: row.labelKey,
        item: null,
      });
      continue;
    }
    for (const item of row.items) {
      entries.push({
        rowId: row.id,
        rowLabelKey: row.labelKey,
        item,
      });
    }
  }
  return entries;
}
